<template>
  <v-container fluid class="overview-page pa-4 pa-md-6">
    <Head>
      <template v-slot:title>
        <v-icon class="mr-1" color="primary">mdi-view-dashboard-outline</v-icon>Operations Overview
      </template>
      <template v-slot:subtitle>
        ภาพรวมการปฏิบัติการ
      </template>
    </Head>

    <!-- KPI Summary Strip -->
    <div class="kpi-strip mb-5">
      <div class="kpi-strip__item">
        <div class="kpi-strip__label">ระบบทั้งหมด</div>
        <div class="kpi-strip__value mono-value">{{ activeServices.length }}</div>
      </div>
      <div class="kpi-strip__item kpi-strip__item--alert">
        <div class="kpi-strip__label">แจ้งเตือน</div>
        <div class="kpi-strip__value mono-value">{{ alertServiceCount }}</div>
      </div>
      <div class="kpi-strip__item kpi-strip__item--ok">
        <div class="kpi-strip__label">ระบบปกติ</div>
        <div class="kpi-strip__value mono-value">{{ normalServiceCount }}</div>
      </div>
      <div class="kpi-strip__item kpi-strip__item--building">
        <div class="kpi-strip__label">อาคาร</div>
        <div class="kpi-strip__value kpi-strip__value--text">{{ building.BuildingName || '—' }}</div>
      </div>
    </div>

    <v-row>
      <!-- Left: Building + Environment -->
      <v-col cols="12" lg="5">
        <IndustrialPanel overline="BUILDING" class="mb-4">
          <template v-slot:title>{{ building.BuildingName }}</template>
          <template v-slot:subtitle>อาคารที่กำลังติดตาม</template>
          <div class="building-panel">
            <div class="building-panel__image">
              <v-img
                :src="building.BuildingImage || require('@/assets/undraw_photos_re_pvh3.svg')"
                height="140"
                cover
                class="rounded-sm"
              />
            </div>
            <div class="building-panel__meta">
              <div v-if="building.BuildingCode" class="building-panel__row">
                <span class="building-panel__key">รหัส</span>
                <span class="building-panel__val mono-value">{{ building.BuildingCode }}</span>
              </div>
              <div class="building-panel__row">
                <span class="building-panel__key">สถานะ</span>
                <v-chip x-small outlined :color="alertServiceCount > 0 ? 'error' : 'success'" class="font-weight-bold">
                  {{ alertServiceCount > 0 ? 'มีการแจ้งเตือน' : 'ปฏิบัติการปกติ' }}
                </v-chip>
              </div>
            </div>
          </div>
        </IndustrialPanel>

        <IndustrialPanel v-if="weather || indoorAQI" overline="ENVIRONMENT" title="คุณภาพอากาศ" class="mb-4">
          <div class="env-stack">
            <div v-if="weather" class="env-block">
              <div class="env-block__header">
                <v-icon small color="info" class="mr-1">mdi-weather-partly-cloudy</v-icon>
                <span>ภายนอกอาคาร</span>
                <v-spacer />
                <span class="env-score mono-value" :class="`env-score--${aqiLevel(weather.aqi)}`">{{ weather.aqi }}</span>
                <span class="env-status">{{ aqiLabel(weather.aqi) }}</span>
              </div>
              <div class="env-metrics">
                <div v-for="metric in outdoorPrimaryMetrics" :key="'out-' + metric.label" class="env-metric">
                  <span class="env-metric__label">{{ metric.label }}</span>
                  <span class="env-metric__value mono-value">{{ metric.value }}</span>
                </div>
              </div>
              <div class="env-meta">
                <v-icon x-small>mdi-clock-outline</v-icon> {{ outdoorTime }}
                <span class="mx-2">·</span>
                <v-icon x-small>mdi-map-marker</v-icon> {{ weather.city.name }}
              </div>
            </div>

            <v-divider v-if="weather && indoorAQI" class="my-3" />

            <div v-if="indoorAQI" class="env-block">
              <div class="env-block__header">
                <v-icon small color="success" class="mr-1">mdi-home-thermometer-outline</v-icon>
                <span>ภายในอาคาร</span>
                <v-spacer />
                <span class="env-score mono-value" :class="`env-score--${aqiLevel(indoorAQI['PM25'])}`">{{ indoorAQI['PM25'] }}</span>
                <span class="env-status">{{ aqiLabel(indoorAQI['PM25']) }} · PM2.5</span>
              </div>
              <div class="env-metrics">
                <div v-for="metric in indoorPrimaryMetrics" :key="'in-' + metric.label" class="env-metric">
                  <span class="env-metric__label">{{ metric.label }}</span>
                  <span class="env-metric__value mono-value">{{ metric.value }}</span>
                </div>
              </div>
              <div class="env-meta">
                <v-icon x-small>mdi-clock-outline</v-icon> {{ indoorTime }}
                <span v-if="assetAQI[0]" class="mx-2">·</span>
                <v-icon v-if="assetAQI[0]" x-small>mdi-map-marker</v-icon>
                {{ assetAQI[0] ? assetAQI[0].location : '' }}
              </div>
            </div>
          </div>
        </IndustrialPanel>
      </v-col>

      <!-- Right: Service List -->
      <v-col cols="12" lg="7">
        <IndustrialPanel overline="SYSTEMS" title="ระบบงานอาคาร" subtitle="เลือกระบบเพื่อดูรายละเอียดการปฏิบัติการ">
          <div class="service-groups">
            <div
              v-for="group in groupedServices"
              :key="group.key"
              class="service-group"
            >
              <div class="service-group__header">{{ group.label }}</div>
              <div class="service-list">
                <component
                  v-for="(item, index) in group.items"
                  :key="group.key + '-' + index"
                  :is="serviceLinkComponent(item)"
                  v-bind="serviceLinkProps(item)"
                  class="service-row"
                  :class="{ 'service-row--alert': !item.systemName && hasAlert(item.id) }"
                >
                  <div class="service-row__icon" :class="`service-row__icon--${item.colorClass}`">
                    <v-icon :color="item.color" size="22">{{ item.icons }}</v-icon>
                  </div>
                  <div class="service-row__body">
                    <div class="service-row__name">{{ item.name }}</div>
                    <div class="service-row__name-th">{{ item.nameTH }}</div>
                  </div>
                  <div class="service-row__status">
                    <template v-if="!item.systemName">
                      <span v-if="hasAlert(item.id)" class="status-pill status-pill--alert">
                        <v-icon x-small class="blink">mdi-alert</v-icon> แจ้งเตือน
                      </span>
                      <span v-else class="status-pill status-pill--ok">
                        <v-icon x-small>mdi-check-circle</v-icon> ปกติ
                      </span>
                    </template>
                    <span v-else class="status-pill status-pill--external">{{ item.systemName }}</span>
                  </div>
                  <v-icon small class="service-row__chevron">mdi-chevron-right</v-icon>
                </component>
              </div>
            </div>
          </div>
        </IndustrialPanel>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Head from '@/components/layout/Head.vue'
import IndustrialPanel from '@/components/common/IndustrialPanel.vue'
import { mapState } from 'vuex'

const COLOR_CLASS_MAP = {
  primary: 'primary',
  blue: 'blue',
  'amber ': 'amber',
  'blue-grey': 'blue-grey',
  'deep-orange ': 'deep-orange',
  green: 'green',
  grey: 'grey',
  red: 'red',
  'teal darken-3': 'teal',
  'light-blue darken-2': 'light-blue',
  'brown ': 'brown',
  teal: 'teal',
  cyan: 'cyan',
}

const SERVICE_GROUPS = {
  energy: { label: 'ไฟฟ้าและพลังงาน', ids: ['20340ea6-88dd-11ee-b9d1-0242ac120003'] },
  water: { label: 'ระบบน้ำ', ids: [
    '20340ea6-88dd-11ee-b9d1-0242ac120004',
    'df062dab-9d10-4c0e-8b86-4a06b94eeb0d',
    '78fe3ec8-4e94-4b19-8869-1c06417a8a19',
    'a07187f5-c788-42f5-8285-8cd5e9c99c0f',
    'be96e932-f9b4-47bb-812c-b32f3f8e6d0d',
  ]},
  climate: { label: 'อากาศและสิ่งแวดล้อม', ids: [
    '5b3c30a7-978d-45e0-beda-0a1bf7ba8048',
    '20340ea6-88dd-11ee-b9d1-0242ac120001',
  ]},
  safety: { label: 'ความปลอดภัย', ids: [
    '20340ea6-88dd-11ee-b9d1-0242ac120002',
    '0090d11d-f990-4a09-98b4-2e55a59e2be7',
  ]},
  operations: { label: 'การดำเนินงาน', ids: [
    '5b3c30a7-978d-45e0-beda-0a1bf7ba8051',
    '25e5df7f-0d2b-4be5-9add-d0be6f2bab57',
    '5b3c30a7-978d-45e0-beda-0a1bf7ba8049',
    '5b3c30a7-978d-45e0-beda-0a1bf7ba8050',
  ]},
}

export default {
  name: 'OverviewView',
  components: {
    Head,
    IndustrialPanel,
  },
  computed: {
    ...mapState('User', ['user', 'tenant', 'roles']),
    ...mapState('Asset', ['assetAQI']),
    ...mapState('Alert', ['alertByService']),
    items() {
      return this.$store.state.Asset.assetAll.filter(el => (el.maintenance === true || el.notify === false))
    },
    building: {
      get() {
        return this.$store.state.User.building
      },
      set(newValue) {
        this.$store.dispatch('User/SET_BUILDING', newValue)
      }
    },
    indoorAQI() {
      if (this.assetAQI.length > 0) {
        return JSON.parse(this.assetAQI[0].data)
      }
      return null
    },
    activeServices() {
      return this.services
        .filter(el => this.building.services.filter(sub => sub.ServiceId === el.id).length)
        .map(item => ({
          ...item,
          colorClass: COLOR_CLASS_MAP[item.color] || 'grey'
        }))
    },
    groupedServices() {
      const grouped = Object.entries(SERVICE_GROUPS).map(([key, group]) => ({
        key,
        label: group.label,
        items: this.activeServices.filter(s => group.ids.includes(s.id)),
      })).filter(g => g.items.length > 0)

      const knownIds = Object.values(SERVICE_GROUPS).flatMap(g => g.ids)
      const others = this.activeServices.filter(s => !knownIds.includes(s.id))
      if (others.length) {
        grouped.push({ key: 'other', label: 'อื่นๆ', items: others })
      }
      return grouped
    },
    alertServiceCount() {
      return this.activeServices.filter(
        s => !s.systemName && this.hasAlert(s.id)
      ).length
    },
    normalServiceCount() {
      return this.activeServices.filter(
        s => !s.systemName && !this.hasAlert(s.id)
      ).length
    },
    outdoorPrimaryMetrics() {
      if (!this.weather) return []
      return [
        { label: 'PM2.5', value: `${this.weather.iaqi.pm25 ? this.weather.iaqi.pm25.v : '-'} µg/m³`, icon: 'mdi-blur' },
        { label: 'PM10', value: `${this.weather.iaqi.pm10 ? this.weather.iaqi.pm10.v : '-'} µg/m³`, icon: 'mdi-weather-fog' },
        { label: 'อุณหภูมิ', value: `${this.weather.iaqi.t ? this.weather.iaqi.t.v : '-'}°C`, icon: 'mdi-thermometer' },
        { label: 'ความชื้น', value: `${this.weather.iaqi.h ? this.weather.iaqi.h.v : '-'}%`, icon: 'mdi-water-percent' },
      ]
    },
    outdoorTime() {
      if (!this.weather) return '-'
      return this.$dayjs(this.weather.time.v * 1000).format('DD MMM YYYY HH:mm')
    },
    indoorPrimaryMetrics() {
      if (!this.indoorAQI) return []
      return [
        { label: 'PM10', value: `${this.indoorAQI['PM10']} µg/m³`, icon: 'mdi-weather-fog' },
        { label: 'อุณหภูมิ', value: `${this.indoorAQI['Temperature']}°C`, icon: 'mdi-thermometer' },
        { label: 'ความชื้น', value: `${this.indoorAQI['Humidity']}%`, icon: 'mdi-water-percent' },
        { label: 'CO₂', value: `${this.indoorAQI['CO2']} ppm`, icon: 'mdi-molecule-co2' },
      ]
    },
    indoorTime() {
      return this.$dayjs().format('DD MMM YYYY HH:mm')
    },
  },
  watch: {
    building() {
      this.airQuality()
      this.refresh()
    },
  },
  async mounted() {
    await this.airQuality()
  },
  data() {
    return {
      services: [
        { name: 'Electrical', nameTH: 'ระบบไฟฟ้า', service: 'Smart Energy', icons: 'mdi-lightning-bolt-circle', navigate: 'electrical', subgroup: false, active: false, id: '20340ea6-88dd-11ee-b9d1-0242ac120003', color: 'primary', alarm: false},
        { name: 'Cold Water', nameTH: 'ระบบน้ำดี', service: 'Cold Water', icons: 'mdi-waves-arrow-up', navigate: 'ColdWater', subgroup: false, active: false, id: '20340ea6-88dd-11ee-b9d1-0242ac120004', color: 'blue', alarm: false },
        { name: 'Soft Water', nameTH: 'ระบบน้ำอ่อน', service: 'Soft Water', icons: 'mdi-grain', navigate: 'SoftWater', subgroup: false, active: false, id: 'df062dab-9d10-4c0e-8b86-4a06b94eeb0d', color: 'amber ', alarm: false },
        { name: 'Waste Water', nameTH: 'ระบบน้ำเสีย', service: 'Waste Water', icons: 'mdi-recycle', navigate: 'WasteWater', subgroup: false, active: false, id: '78fe3ec8-4e94-4b19-8869-1c06417a8a19', color: 'blue-grey', alarm: false },
        { name: 'Hot Water', nameTH: 'ระบบน้ำร้อน', service: 'Hot Water', icons: 'mdi-hot-tub', navigate: 'HotWater', subgroup: false, active: false, id: 'a07187f5-c788-42f5-8285-8cd5e9c99c0f', color: 'deep-orange ', alarm: false },
        { name: 'Rain Water', nameTH: 'ระบบน้ำฝน', service: 'Rain Water', icons: 'mdi-weather-pouring', navigate: 'RainWater', subgroup: false, active: false, id: 'be96e932-f9b4-47bb-812c-b32f3f8e6d0d', color: 'green', alarm: false },
        { name: 'HVAC', nameTH: 'ระบบปรับอากาศ', service: 'Smart Level', icons: 'mdi-hvac', navigate: 'hvac', subgroup: false, active: true, id: '5b3c30a7-978d-45e0-beda-0a1bf7ba8048', color: 'grey', alarm: false },
        { name: 'FireAlarm', nameTH: 'ระบบแจ้งเหตุเพลิงไหม้', service: 'Smart Alarm', icons: 'mdi-fire', navigate: 'fire_alarm', subgroup: true, active: true, id: '20340ea6-88dd-11ee-b9d1-0242ac120002', color: 'red', alarm: false },
        { name: 'AirQuality', nameTH: 'ระบบติดตามคุณภาพอากาศ', service: 'Air Quality', icons: 'mdi-air-filter', navigate: 'air_quality', subgroup: true, active: true, id: '20340ea6-88dd-11ee-b9d1-0242ac120001', color: 'teal darken-3', alarm: false },
        { name: 'Refrigerator', nameTH: 'ระบบติดตามอุณหภูมิตู้แช่ยา', service: 'Refrigerator', icons: 'mdi-fridge-industrial', navigate: 'medicine_refrigerator', subgroup: true, active: true, id: '0090d11d-f990-4a09-98b4-2e55a59e2be7', color: 'light-blue darken-2', alarm: false },
        { name: 'Maintenance', nameTH: 'รายการอุปกรณ์ปิดปรับปรุง', service: 'Device Status', icons: 'mdi-volume-off', navigate: 'maintenance', subgroup: false, active: true, id: '5b3c30a7-978d-45e0-beda-0a1bf7ba8051', color: 'brown ', systemName: ' '},
        { name: 'F1 Dashboard', nameTH: 'ภาพรวมการดำเนินงาน', service: 'CMMS Report', icons: 'mdi-view-dashboard', navigate: 'cmms', subgroup: false, active: true, id: '25e5df7f-0d2b-4be5-9add-d0be6f2bab57', alarm: false, color: 'teal', systemName: 'F1 Dashboard' },
        { name: 'CMMS', nameTH: 'ระบบแจ้งซ่อมและบำรุงรักษา', service: 'CMMS', icons: 'mdi-tools', navigate: 'cmms', subgroup: false, active: true, id: '5b3c30a7-978d-45e0-beda-0a1bf7ba8049', color: 'teal', alarm: false, url: 'https://app.servicesf1.com/', systemName: 'Services F1' },
        { name: 'Work Permit', nameTH: 'ระบบใบอนุญาตเข้าพื้นที่', service: 'Work Permit', icons: 'mdi-security', navigate: 'workpermit', subgroup: false, active: true, id: '5b3c30a7-978d-45e0-beda-0a1bf7ba8050', color: 'cyan', alarm: false, url: 'https://workpermit.smartfem.app', systemName: 'Work Permit' },
      ],
      weather: null
    }
  },
  methods: {
    hasAlert(serviceId) {
      return (this.alertByService[serviceId]) ? this.alertByService[serviceId].length : 0
    },
    serviceLinkComponent(item) {
      return (item.name === 'CMMS' || item.name === 'Work Permit') ? 'a' : 'router-link'
    },
    serviceLinkProps(item) {
      if (item.name === 'CMMS' || item.name === 'Work Permit') {
        return { href: item.url, target: '_blank', rel: 'noopener' }
      }
      return { to: { name: item.navigate } }
    },
    aqiLevel(value) {
      if (value < 50) return 'good'
      if (value < 100) return 'moderate'
      if (value < 200) return 'unhealthy'
      return 'hazardous'
    },
    aqiLabel(value) {
      const levels = {
        good: 'อากาศดี',
        moderate: 'ปานกลาง',
        unhealthy: 'ไม่ดีต่อสุขภาพ',
        hazardous: 'อันตราย',
      }
      return levels[this.aqiLevel(value)]
    },
    async airQuality() {
      if (this.building.StationId) {
        const response = await fetch(`https://api.waqi.info/feed/${this.building.StationId}/?token=d88c969eb1941e4e73c7907ef8ae36ed88420b37`)
        const weather = await response.json()
        this.weather = weather.data
      } else {
        this.weather = null
      }
    },
    refresh() {
      this.$store.dispatch('Asset/GET_ALL_ASSET', this.building).catch((err) => {
        console.log(err)
      })
    }
  },
}
</script>

<style scoped>
.overview-page {
  max-width: 1600px;
}

/* ── KPI Strip ── */
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

@media (min-width: 960px) {
  .kpi-strip {
    grid-template-columns: repeat(4, 1fr);
  }
}

.kpi-strip__item {
  background: var(--surface-panel, #fff);
  border: 1px solid var(--border-subtle, #D1D5DB);
  border-radius: 4px;
  padding: 14px 16px;
  border-left: 3px solid var(--accent-amber, #D97706);
}

.kpi-strip__item--alert { border-left-color: #DC2626; }
.kpi-strip__item--ok { border-left-color: #059669; }
.kpi-strip__item--building { border-left-color: #475569; }

.kpi-strip__label {
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted, #6B7280);
}

.kpi-strip__value {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary, #111827);
  margin-top: 4px;
  line-height: 1.2;
}

.kpi-strip__value--text {
  font-family: 'IBM Plex Sans Thai', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Building Panel ── */
.building-panel {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.building-panel__image {
  flex: 0 0 140px;
  width: 140px;
}

.building-panel__meta {
  flex: 1;
  min-width: 0;
}

.building-panel__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-subtle, #D1D5DB);
}

.building-panel__row:last-child {
  border-bottom: none;
}

.building-panel__key {
  font-size: 0.75rem;
  color: var(--text-muted, #6B7280);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.building-panel__val {
  font-size: 0.875rem;
  font-weight: 600;
}

/* ── Environment ── */
.env-block__header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary, #111827);
  margin-bottom: 12px;
}

.env-score {
  font-size: 1.25rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  margin-right: 6px;
}

.env-score--good { background: rgba(5, 150, 105, 0.12); color: #059669; }
.env-score--moderate { background: rgba(217, 119, 6, 0.12); color: #D97706; }
.env-score--unhealthy { background: rgba(234, 88, 12, 0.12); color: #EA580C; }
.env-score--hazardous { background: rgba(220, 38, 38, 0.12); color: #DC2626; }

.env-status {
  font-size: 0.72rem;
  color: var(--text-muted, #6B7280);
}

.env-metrics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.env-metric {
  background: var(--surface-base, #F4F5F7);
  border: 1px solid var(--border-subtle, #D1D5DB);
  border-radius: 4px;
  padding: 8px 10px;
}

.env-metric__label {
  display: block;
  font-size: 0.65rem;
  color: var(--text-muted, #6B7280);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.env-metric__value {
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 2px;
}

.env-meta {
  margin-top: 10px;
  font-size: 0.72rem;
  color: var(--text-muted, #6B7280);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

/* ── Service List ── */
.service-group + .service-group {
  margin-top: 20px;
}

.service-group__header {
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-amber, #D97706);
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border-subtle, #D1D5DB);
}

.service-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 10px;
  border-bottom: 1px solid var(--border-subtle, #D1D5DB);
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease;
  cursor: pointer;
}

.service-row:last-child {
  border-bottom: none;
}

.service-row:hover {
  background: var(--surface-base, #F4F5F7);
}

.service-row--alert {
  background: rgba(220, 38, 38, 0.04);
  border-left: 3px solid #DC2626;
  padding-left: 7px;
}

.service-row__icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle, #D1D5DB);
  border-radius: 4px;
  background: var(--surface-base, #F4F5F7);
}

.service-row__icon--primary { border-color: #D97706; }
.service-row__icon--blue { border-color: #0EA5E9; }
.service-row__icon--amber { border-color: #D97706; }
.service-row__icon--blue-grey { border-color: #64748B; }
.service-row__icon--deep-orange { border-color: #EA580C; }
.service-row__icon--green { border-color: #059669; }
.service-row__icon--grey { border-color: #9CA3AF; }
.service-row__icon--red { border-color: #DC2626; }
.service-row__icon--teal { border-color: #0D9488; }
.service-row__icon--light-blue { border-color: #0284C7; }
.service-row__icon--brown { border-color: #92400E; }
.service-row__icon--cyan { border-color: #0891B2; }

.service-row__body {
  flex: 1;
  min-width: 0;
}

.service-row__name {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.3;
}

.service-row__name-th {
  font-size: 0.75rem;
  color: var(--text-muted, #6B7280);
  margin-top: 1px;
}

.service-row__status {
  flex-shrink: 0;
}

.service-row__chevron {
  flex-shrink: 0;
  color: var(--text-muted, #9CA3AF) !important;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid;
  white-space: nowrap;
}

.status-pill--ok {
  color: #059669;
  border-color: #059669;
  background: rgba(5, 150, 105, 0.08);
}

.status-pill--alert {
  color: #DC2626;
  border-color: #DC2626;
  background: rgba(220, 38, 38, 0.08);
}

.status-pill--external {
  color: #475569;
  border-color: #94A3B8;
  background: rgba(71, 85, 105, 0.06);
}

@keyframes argh-my-eyes {
  0%, 49%, 100% { color: #FECACA; }
  50%, 99% { color: #DC2626; }
}

.blink {
  animation: argh-my-eyes 2s infinite !important;
}
</style>
